import PropTypes, { func } from "prop-types";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Wrapper from "../../helpers/Wrapper";
import { BubbleChartSampleData } from "../../../data/chartsDataSamples";
import { CountryCode, CountryCurrency } from "../../../constants/countryCurrency";
import { USER_COUNTRY } from "../../../constants/constants";
import { useSelector } from "react-redux";
import { formatNumberForTooltip } from "../../../utils/NumberFormatter";

am5.addLicense("AM5C258896422");

const BubbleChartSizeShapeDynamic = ({
    chartId,
    chartClass,
    chartData,
    chartLabels,
    riMarket,
    riMarketIDFC,
    riMarketIDFCLabels,
    valueDropdown,
    valueDropdown1,
    dimensionDropdown
}) => {

    // USER DATA
    const userData = useSelector((state) => state.userData.user[0]);

    let userCountryCode = userData?.country_code ?? "IN"

    let country = {}

    CountryCode.map((data) => {
        if (data.countryCode === userCountryCode) {
            country = data
        }
    })

    useLayoutEffect(() => {

        if (riMarket === undefined) {
            riMarket = false
        }

        // Create root element
        // https://www.amcharts.com/docs/v5/getting-started/#Root_element
        var root = am5.Root.new(chartId);

        // Set themes
        // https://www.amcharts.com/docs/v5/concepts/themes/
        root.setThemes([
            am5themes_Animated.new(root)
        ]);
        root.interfaceColors.set("grid", am5.color(0xffffff)); // to chang the color of grid lines


        // to format numbers
        root.numberFormatter.setAll({
            numberFormat: "#.##a",

            // Group only into M (millions), and B (billions)
            bigNumberPrefixes: [
                { number: 1e3, suffix: "K" },
                { number: 1e6, suffix: "M" },
                { number: 1e9, suffix: "B" },
            ],

            // Do not use small number prefixes at all
            smallNumberPrefixes: [],
        });

        // Create chart
        // https://www.amcharts.com/docs/v5/charts/xy-chart/
        var chart = root.container.children.push(am5xy.XYChart.new(root, {
            pinchZoomX: "none",
            pinchZoomY: "none",
            panX: false,
            panY: false,
            panXY: false,
            wheelX: "none",
            wheelY: "none",
            layout: root.verticalLayout, // to add chart and legends in vertical
            paddingRight: 0,
            paddingTop: 5,
            paddingBottom: 0,
            paddingLeft: 0,
            maxTooltipDistance: -1,
        }));

        chart.get("colors").set("step", 2);

        // Create axes
        // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/

        var xRenderer = am5xy.AxisRendererX.new(root, {
            minGridDistance: 50,
            stroke: am5.color(0x707070),
            strokeWidth: 1,
            strokeOpacity: 1,
        });

        var xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
            // tooltip: am5.Tooltip.new(root, {}),
            renderer: xRenderer,
        }));

        xAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "truncate", // options: "none"/"wrap"/"fit"
            maxWidth: 150,
            textAlign: "center",
            fontSize: 12,
            fontFamily: "Ubuntu",
            fill: am5.color(0x757575),
            fontWeight: "400",
        });

        var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {
                // stroke: am5.color(0x757575),
                stroke: am5.color(0x707070),
                // strokeWidth: 0.3,
                strokeWidth: 1,

                strokeOpacity: 1,
            }),
            // tooltip: am5.Tooltip.new(root, {})
        }));

        yAxis.get("renderer").labels.template.setAll({
            oversizedBehavior: "truncate", // options: "none" / "wrap" / "fit" / "truncate"
            maxWidth: 150,
            textAlign: "center",
            fontSize: 12,
            fontFamily: "Ubuntu",
            fill: am5.color(0x757575),
            fontWeight: "400",
        });


        const addCurrencySymbols = (data) => {
            const currencyMetrics = ["Spends", "TV Spends", "All Digital Spends"];
            if (currencyMetrics.includes(data)) {
                return country.currencySymbol
            } else {
                return ""
            }
        }

        const convertPrefixes = (name) => {
            const tonnesMetrics = ["Monthly Sales Vol."];
            const percentMetrics = ["Monthly Sales Vol. % Change", "TV Spends % Change", "Market Share", "Total Spends %", "Monthly Sales Vol. %"];


            if (percentMetrics.includes(name)) {
                return "%"
            } else if (tonnesMetrics.includes(name)) {
                return " T"
            } else {
                return ""
            }
        }


        function wordFormatter(string) {
            const temp = ['growth'];
            if (temp.includes(string)) {
                return 'Sales Vol. Change'
            } else {
                return string;
            }

        }

        function uppercaseAllLetters(data) {
            const temp = ['Grps']

            if (temp.includes(data)) {
                let temp = '';
                let lastChar = data.slice(data.length - 1)
                for (var i = 0; i < data.length - 1; i++) {
                    temp += data.charAt(i).toUpperCase()
                }
                return temp + lastChar;
            } else {
                return data;
            }
        }
        function capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        }


        function createSeries(value, valuex, valuey, valuexLabel, valueyLabel, market, growth, color, children) {
            let childrenObj = {};

            if (children === undefined || children.length === 0) {
            } else {
                childrenObj = children.map((child, index) => {
                    return {
                        childLabel: child.label,
                        childValue: child.value,
                    };
                });
            }

            // Create series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
            var series0 = chart.series.push(am5xy.LineSeries.new(root, {
                calculateAggregates: true,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: valuey,
                valueXField: valuex,
                valueField: value,
                valuexLabel: valuexLabel,
                valueyLabel: valueyLabel,
                market: market,
                growthValue: growth,
                childrenObj
            }));

            var tooltip = series0.set(
                "tooltip",
                am5.Tooltip.new(root, {
                    getFillFromSprite: false,
                    getStrokeFromSprite: false,
                    autoTextColor: false,
                    pointerOrientation: "horizontal",
                    position: "pointer",
                })
            );

            tooltip.get("background").setAll({
                fill: am5.color(0x213345),
                // maxWidth: 200,
                oversizedBehavior: "truncate",
                cornerRadius: 5,
            });

            tooltip.label.setAll({
                fill: am5.color(0xffffff),
                fontSize: 12,
                fontFamily: "Ubuntu",
                fontWeight: "bold"
            });

            tooltip.label.adapters.add("text", function (text, target) {
                chart.series.each(function (series) {
                    const dataItem = series.get("tooltipDataItem");
                    if (!dataItem) return;
                    const ctx = dataItem.dataContext || {};

                    // Dropdowns from props
                    const dimLabel = dimensionDropdown?.label || "Dimension";
                    const dimValue = dimensionDropdown?.value;
                    const valLabel = valueDropdown?.label || "Value";
                    const valValue = valueDropdown?.value;
                    const val1Label = valueDropdown1?.label || "Value 1";
                    const val1Value = valueDropdown1?.value;

                    let tooltipText = "";

                    if (ctx[dimValue] !== undefined && ctx[dimValue] !== "") {
                        tooltipText += `[bold]● ${dimLabel}: ${ctx[dimValue]}[/]\n`;
                    }
                    if (ctx[valValue] !== undefined && ctx[valValue] !== "") {
                        tooltipText += `[bold]● ${valLabel}: ${formatNumberForTooltip(ctx[valValue], valLabel)}[/]\n`;
                    }
                    if (
                        val1Value &&
                        val1Value !== valValue &&
                        ctx[val1Value] !== undefined &&
                        ctx[val1Value] !== ""
                    ) {
                        tooltipText += `[bold]● ${val1Label}: ${formatNumberForTooltip(ctx[val1Value], val1Label)}[/]\n`;
                    }
                    const marketShareKey = Object.keys(ctx).find(k =>
                        k.toLowerCase().includes("market_share")
                    );
                    if (marketShareKey && ctx[marketShareKey] !== "") {
                        tooltipText += `[bold]● Market Share: ${formatNumberForTooltip(ctx[marketShareKey], "Market Share")}[/]\n`;
                    }
                    const growthKey = Object.keys(ctx).find(k =>
                        k.toLowerCase().includes("growth")
                    );
                    if (growthKey && ctx[growthKey] !== "") {
                        tooltipText += `[bold]● Growth: ${formatNumberForTooltip(ctx[growthKey], "Growth")}[/]\n`;
                    }

                    text = tooltipText.trim();
                });

                return text;
            });

            // if (riMarketIDFC === true) {
            //     tooltip.label.setAll({
            //         text: "[bold]{state_name}[/]",
            //         fill: am5.color(0xffffff),
            //         fontSize: 13,
            //     });
            // } else {
            //     tooltip.label.setAll({
            //         text: "[bold]{market}[/]",
            //         fill: am5.color(0xffffff),
            //         fontSize: 13,
            //     });
            // }


            // // ...inside createSeries, after tooltip.label.setAll({...})...
            // tooltip.label.adapters.add("text", function (text, target) {
            //     chart.series.each(function (series) {
            //         const dataItem = series.get("tooltipDataItem");
            //         if (!dataItem) return;
            //         const ctx = dataItem.dataContext || {};

            //         // Dropdowns from props
            //         const dimLabel = dimensionDropdown?.label || "Dimension";
            //         const dimValue = dimensionDropdown?.value;
            //         const valLabel = valueDropdown?.label || "Value";
            //         const valValue = valueDropdown?.value;
            //         const val1Label = valueDropdown1?.label || "Value 1";
            //         const val1Value = valueDropdown1?.value;

            //         let tooltipText = "";

            //         // Dimension
            //         if (ctx[dimValue] !== undefined && ctx[dimValue] !== "") {
            //             tooltipText += `● ${dimLabel}: [bold]${ctx[dimValue]}[/]\n`;
            //         }

            //         // Value 1
            //         if (ctx[valValue] !== undefined && ctx[valValue] !== "") {
            //             tooltipText += `● ${valLabel}: [bold]${tooltipNumberFormatting(valLabel, ctx[valValue], country)}[/]\n`;
            //         }

            //         // Value 2 (if different from Value 1)
            //         if (
            //             val1Value &&
            //             val1Value !== valValue &&
            //             ctx[val1Value] !== undefined &&
            //             ctx[val1Value] !== ""
            //         ) {
            //             tooltipText += `● ${val1Label}: [bold]${tooltipNumberFormatting(val1Label, ctx[val1Value], country)}[/]\n`;
            //         }

            //         // Market Share (try multiple possible keys)
            //         const marketShareKey = Object.keys(ctx).find(k =>
            //             k.toLowerCase().includes("market_share")
            //         );
            //         if (marketShareKey && ctx[marketShareKey] !== "") {
            //             tooltipText += `● Market Share: [bold]${tooltipNumberFormatting("Market Share", ctx[marketShareKey], country)}[/]\n`;
            //         }

            //         // Growth (try multiple possible keys)
            //         const growthKey = Object.keys(ctx).find(k =>
            //             k.toLowerCase().includes("growth")
            //         );
            //         if (growthKey && ctx[growthKey] !== "") {
            //             tooltipText += `● Growth: [bold]${tooltipNumberFormatting("Growth", ctx[growthKey], country)}[/]\n`;
            //         }

            //         text = tooltipText.trim();
            //     });

            //     return text;
            // });


            // Add bullet
            // https://www.amcharts.com/docs/v5/charts/xy-chart/series/#Bullets
            var circleTemplate = am5.Template.new({});

            circleTemplate.adapters.add("fill", function (fill, target) {
                var dataItem = target.dataItem;
                if (dataItem) {
                    return am5.Color.fromString(dataItem.dataContext.color);
                }
                return fill
            });

            series0.bullets.push(function () {
                var bulletCircle = am5.Circle.new(root, {
                    radius: 5,
                    fill: series0.get("fill"),
                    fillOpacity: 1
                }, circleTemplate);
                return am5.Bullet.new(root, {
                    sprite: bulletCircle
                });
            });

            // series0.bullets.push(function () {
            //     var graphics = am5.Circle.new(root, { fill: series0.get("growthValue") >= 0 ? "0x4cc3fd" : "0xe96e7a" }, circleTemplate);
            //     return am5.Bullet.new(root, { sprite: graphics });
            // });

            // Add heat rule
            // https://www.amcharts.com/docs/v5/concepts/settings/heat-rules/
            series0.set("heatRules", [{
                target: circleTemplate,
                min: 3,
                max: 35,
                dataField: "value",
                key: "radius"
            }]);

            series0.strokes.template.set("strokeOpacity", 0);


            // Add cursor
            // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
            chart.set("cursor", am5xy.XYCursor.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                snapToSeries: [series0]
            }));

            var data = chartData;

            series0.data.setAll(data);
            // series1.data.setAll(data);


            // Make stuff animate on load
            // https://www.amcharts.com/docs/v5/concepts/animations/
            series0.appear(1000);
        }


        chartLabels.map((label, index) => {
            createSeries(
                label.value,
                label.valuex,
                label.valuey,
                label.valuexLabel,
                label.valueyLabel,
                label.market,
                label.growth,
                label.color,
                label.children,
                index
            );
        });

        chart.appear(1000, 100);

        return () => {
            root.dispose();
        };
    }, [chartData]);

    return (
        <Wrapper>
            {/* <div id={chartId} className={chartClass}></div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "32px",
                    marginTop: "20px",
                    width: "100%",
                    
                }}
            >
                <span style={{ color: "#213345",   fontSize: 12 }}>
                    Size = Market Share%
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                        style={{
                            display: "inline-block",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: "#038673",
                        }}
                    ></span>
                    <span style={{ color: "#213345",  fontSize: 12 }}>
                       {valueDropdown?.label || "Sales Value "}% Growth
                    </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                        style={{
                            display: "inline-block",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: "#D51F30",
                        }}
                    ></span>
                    <span style={{ color: "#213345",   fontSize: 12 }}>
                                {valueDropdown?.label ? valueDropdown.label.replace("Growth", "Degrowth") : "Sales Value "}% Degrowth

                    </span>
                </span>
            </div> */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <div
                    id={chartId}
                    className={chartClass}
                    style={{
                        width: "100%",        // Chart uses all parent width
                        minHeight: "340px",   // Ensures good vertical space
                        background: "#fff",
                        borderRadius: "12px",
                    }}
                ></div>
                {/* Legend centered below chart */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "32px",
                        marginTop: "20px",
                        width: "100%",
                    }}
                >
                    <span style={{ color: "#213345", fontSize: 12 }}>
                        Size = Market Share%
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span
                            style={{
                                display: "inline-block",
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: "#038673",
                            }}
                        ></span>
                        <span style={{ color: "#213345", fontSize: 12 }}>
                            {valueDropdown?.label || "Sales Value"}% Growth
                        </span>
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span
                            style={{
                                display: "inline-block",
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: "#D51F30",
                            }}
                        ></span>
                        <span style={{ color: "#213345", fontSize: 12 }}>
                            {valueDropdown?.label
                                ? valueDropdown.label.replace("Growth", "Degrowth")
                                : "Sales Value"}% Degrowth
                        </span>
                    </span>
                </div>
            </div>
        </Wrapper>
    );
};

BubbleChartSizeShapeDynamic.propTypes = {
    chartId: PropTypes.string,
    chartClass: PropTypes.string,
    chartData: PropTypes.array,
    chartLabels: PropTypes.array,
    riMarket: PropTypes.bool,
    riMarketIDFC: PropTypes.bool,
    riMarketIDFCLabels: PropTypes.string,
    valueDropdown: PropTypes.object,
    valueDropdown1: PropTypes.object,
    dimensionDropdown: PropTypes.object,
};

export default BubbleChartSizeShapeDynamic;
